import * as React from 'react';
import { Link } from 'react-router-dom';

import { Box, Stack, Typography } from '@mui/material';

import styled from '@emotion/styled';

import LazyAvatar from '../../../components/Avatar/LazyAvatar/LazyAvatar';
import Button from '../../../components/Button/Button';
import Tooltip from '../../../components/Dialogs/CollabsFilters/ToolTip';
import SvgIcon from '../../../components/Icons';
import { formatNumber } from '../../../utils/formatter';

type DashboardAvatar = {
  src?: string;
  placeholderSrc?: string;
  alt?: string;
};

type DashboardContentThumb = {
  src?: string;
  placeholderSrc?: string;
  videoSrc?: string;
};

type DashboardCardCta = {
  label: string;
  to: string;
};

type DashboardCardProps = {
  label: string;
  count?: number;
  value?: React.ReactNode;
  subText?: React.ReactNode;
  subTextEmphasized?: boolean;
  subscriptionPlan?: string;
  onClick?: () => void;
  avatars?: DashboardAvatar[];
  avatarsExtraCount?: number;
  contentThumbs?: DashboardContentThumb[];
  dark?: boolean;
  cta?: DashboardCardCta;
  hideLabel?: boolean;
};

const DashboardCard: React.FC<DashboardCardProps> = ({
  label,
  count = 0,
  value,
  subText,
  subTextEmphasized = false,
  subscriptionPlan = '',
  onClick,
  avatars,
  avatarsExtraCount = 0,
  contentThumbs,
  dark = false,
  cta,
  hideLabel = false,
}) => {
  const isFavorites = label.toLowerCase() === 'favorites';
  const formattedCount = formatNumber(count);
  const hasFavourites = +formattedCount > 0;
  const showUpgradeTooltip =
    isFavorites && subscriptionPlan !== 'plus' && hasFavourites;

  const subTextColor = '#6B6B6B';
  const avatarBorderColor = dark ? '#F5F5F5' : '#fff';

  const card = (
    <Box
      onClick={onClick}
      sx={{
        // Basis rather than `flex: 1`: combined with flexWrap on the row, this
        // lets the cards drop to a second line instead of compressing past
        // legibility on a narrow laptop.
        flex: '1 1 240px',
        minWidth: 0,
        border: dark ? '1px solid #C9C9C9' : '1px solid #ECECEC',
        borderRadius: 2,
        p: 3,
        backgroundColor: dark ? '#F5F5F5' : undefined,
        display: 'flex',
        flexDirection: 'column',
        ...(onClick
          ? {
              cursor: 'pointer',
              transition: 'border-color 0.2s, box-shadow 0.2s',
              '&:hover': {
                borderColor: dark ? '#A8A8A8' : '#D0D0D0',
                boxShadow: '0 2px 10px 0 rgba(0, 0, 0, 0.06)',
              },
            }
          : {}),
      }}
    >
      <Stack spacing={1} sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: '34px', fontWeight: 600, lineHeight: 1.1 }}>
          {value ?? formattedCount}
        </Typography>

        {!hideLabel && (
          <Typography variant='body2' noWrap sx={{ fontWeight: 500 }}>
            <span>{label}</span>
          </Typography>
        )}

        {contentThumbs && contentThumbs.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', pt: 0.5 }}>
            {contentThumbs.slice(0, 9).map((thumb, index) => (
              <Box
                key={index}
                sx={{
                  position: 'relative',
                  width: 24,
                  height: 36,
                  borderRadius: '4px',
                  overflow: 'hidden',
                  backgroundColor: '#F1EFF9',
                  flexShrink: 0,
                }}
              >
                <LazyAvatar
                  variant='square'
                  src={thumb.src}
                  placeholderSrc={thumb.placeholderSrc}
                  sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: 0,
                    '& img': { objectFit: 'cover' },
                  }}
                />
              </Box>
            ))}
          </Box>
        )}

        {avatars && avatars.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', pt: 0.5 }}>
            {avatars.map((avatar, index) => (
              <LazyAvatar
                key={index}
                variant='circular'
                src={avatar.src}
                placeholderSrc={avatar.placeholderSrc}
                alt={avatar.alt}
                sx={{
                  width: 28,
                  height: 28,
                  border: `2px solid ${avatarBorderColor}`,
                  ml: index === 0 ? 0 : '-8px',
                }}
              />
            ))}
            {avatarsExtraCount > 0 && (
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  ml: '-8px',
                  borderRadius: '50%',
                  border: `2px solid ${avatarBorderColor}`,
                  backgroundColor: '#F1EFF9',
                  color: '#765BC6',
                  fontSize: '11px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                +{avatarsExtraCount}
              </Box>
            )}
          </Box>
        )}

        {subText && (
          <Typography
            variant={subTextEmphasized ? 'body2' : undefined}
            sx={
              subTextEmphasized
                ? { fontWeight: 500, lineHeight: 1.4 }
                : { fontSize: '13px', color: subTextColor, lineHeight: 1.4 }
            }
          >
            {subText}
          </Typography>
        )}
      </Stack>

      {cta && (
        <Button
          component={Link}
          to={cta.to}
          variant='contained'
          fullWidth
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          sx={{
            mt: 2,
            fontSize: '14px',
            fontWeight: 500,
            backgroundColor: '#1a1a1a',
            color: '#ffffff',
            '&:hover': { backgroundColor: '#333333' },
          }}
        >
          {cta.label}
        </Button>
      )}
    </Box>
  );

  if (showUpgradeTooltip) {
    return (
      <Tooltip
        arrow
        title={
          <React.Fragment>
            <PremiumBox>
              <PremiumBoxSidebar></PremiumBoxSidebar>
              <TitleBox>
                Unlock
                <SvgIcon
                  name='Plus'
                  sx={{
                    fill: 'url(#linear-gradient)',
                    padding: '6px 0 0 0',
                    margin: '0px 6px',
                  }}
                  fontSize='small'
                />
                <Link
                  style={{
                    fontSize: '15px',
                    color: '#E28DDE',
                    fontWeight: 600,
                    textDecoration: 'underline',
                  }}
                  to={'/plans'}
                >
                  Plus
                </Link>
                {' '}to see who saved you and reach out to collaborate
              </TitleBox>
            </PremiumBox>
          </React.Fragment>
        }
      >
        {card}
      </Tooltip>
    );
  }

  return card;
};

export default DashboardCard;

const PremiumBox = styled.div`
  display: flex;
  width: 100%;
  background-color: #ffffff;
  border-radius: 5px;
`;

const PremiumBoxSidebar = styled.div`
  height: inherit;
  width: 12px;
  border-radius: 5px;
  background-image: linear-gradient(to bottom right, #b26ff3, #c991ff);
`;

const TitleBox = styled.p`
  font-size: 15px;
  font-weight: 400;
  color: #000;
  padding: 0px 20px;
`;
